/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit} from '@angular/core';

import {Subscription} from 'rxjs';

import {GuidedTourService, GuidedTourState, GuidedTourStep} from './guided-tour.service';

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Renders the active guided tour: a dimming backdrop with a spotlight cut-out around the
 * step target, plus a popover describing the step. Place a single instance at the app root.
 */
@Component({
  selector: 'gp-guided-tour',
  templateUrl: './guided-tour.component.html',
  styleUrls: ['./guided-tour.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GuidedTourComponent implements OnInit, OnDestroy {

  /** Padding (px) added around the highlighted element. */
  private static readonly SPOTLIGHT_PADDING = 6;
  /** Gap (px) between the spotlight and the popover. */
  private static readonly POPOVER_GAP = 14;
  private static readonly POPOVER_WIDTH = 320;
  /** Estimated popover height (px), used to keep it on screen before it can be measured. */
  private static readonly POPOVER_ESTIMATED_HEIGHT = 220;
  /** Minimum margin (px) kept between the popover and the viewport edges. */
  private static readonly VIEWPORT_MARGIN = 16;
  /** Max attempts to find a step target before skipping it (covers async-loaded views). */
  private static readonly TARGET_MAX_RETRIES = 25;
  /** Delay (ms) between target lookup attempts. */
  private static readonly TARGET_RETRY_DELAY = 150;

  public state: GuidedTourState = null;
  /** Spotlight box around the target, or null when the step has no target (centered popover). */
  public spotlight: Box = null;
  public popover: { top: number; left: number; arrow: 'left' | 'top' | 'none' } = {top: 0, left: 0, arrow: 'none'};

  /** The step currently shown, used to run beforeShow/onLeave hooks exactly once per step. */
  private activeStep: GuidedTourStep = null;
  /** Identifies the current resolution run, so stale retry timers are ignored after a step change. */
  private resolveToken = 0;

  private subscription: Subscription;

  constructor(private guidedTourService: GuidedTourService, private cd: ChangeDetectorRef) {
  }

  public ngOnInit(): void {
    this.subscription = this.guidedTourService.state$.subscribe(state => {
      this.handleStepChange(state?.step ?? null);
      this.state = state;
      // Resolve the target with retries so async-loaded views (e.g. the org edit form) settle.
      this.resolveStep(GuidedTourComponent.TARGET_MAX_RETRIES, 0);
    });
  }

  public ngOnDestroy(): void {
    this.activeStep?.onLeave?.();
    this.subscription?.unsubscribe();
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  public onViewportChange(): void {
    if (this.state) {
      this.reposition();
    }
  }

  public next(): void {
    this.guidedTourService.next();
  }

  public previous(): void {
    this.guidedTourService.previous();
  }

  public skip(): void {
    this.guidedTourService.complete();
  }

  /** Runs onLeave for the previous step and beforeShow for the new one, exactly once per change. */
  private handleStepChange(step: GuidedTourStep): void {
    if (step === this.activeStep) {
      return;
    }
    this.activeStep?.onLeave?.();
    this.activeStep = step;
    this.resolveToken++;
    this.activeStep?.beforeShow?.();
  }

  /**
   * Resolves the current step's target, retrying for a while to cover targets that appear
   * asynchronously (e.g. after a view switch / HTTP load). Skips the step once retries run out.
   */
  private resolveStep(retriesLeft: number, delay: number): void {
    const token = this.resolveToken;
    setTimeout(() => {
      // Abort if the step changed (or tour ended) while waiting.
      if (token !== this.resolveToken || !this.state) {
        return;
      }

      if (!this.stepHasTarget()) {
        this.centerPopover();
        this.cd.markForCheck();
        return;
      }

      const box = this.computeTargetBox();
      if (box) {
        this.applyBox(box);
        this.cd.markForCheck();
        return;
      }

      if (retriesLeft > 0) {
        this.resolveStep(retriesLeft - 1, GuidedTourComponent.TARGET_RETRY_DELAY);
        return;
      }

      // Target never appeared: skip the step so the tour never points at empty space.
      if (this.state.isLast) {
        this.guidedTourService.complete();
      } else {
        this.guidedTourService.goTo(this.state.index + 1);
      }
    }, delay);
  }

  /** Recomputes the spotlight/popover for the current target on scroll/resize (no skipping). */
  private reposition(): void {
    if (!this.stepHasTarget()) {
      this.centerPopover();
      this.cd.markForCheck();
      return;
    }
    const box = this.computeTargetBox();
    if (box) {
      this.applyBox(box);
    }
    this.cd.markForCheck();
  }

  /** Whether the current step defines a target (a selector or a getRect function). */
  private stepHasTarget(): boolean {
    const step = this.state.step;
    return !!(step?.selector || step?.getRect);
  }

  /** Resolves the current step's target rectangle (from getRect or a visible selector match). */
  private computeTargetBox(): Box {
    const step = this.state.step;
    if (step?.getRect) {
      const rect = step.getRect();
      return rect && rect.width > 0 && rect.height > 0
        ? {top: rect.top, left: rect.left, width: rect.width, height: rect.height}
        : null;
    }
    if (step?.selector) {
      const target = this.findVisibleTarget(step.selector);
      if (!target) {
        return null;
      }
      const rect = target.getBoundingClientRect();
      return {top: rect.top, left: rect.left, width: rect.width, height: rect.height};
    }
    return null;
  }

  private applyBox(box: Box): void {
    const pad = GuidedTourComponent.SPOTLIGHT_PADDING;
    this.spotlight = {
      top: box.top - pad,
      left: box.left - pad,
      width: box.width + pad * 2,
      height: box.height + pad * 2
    };
    this.positionPopoverNextTo(this.spotlight);
  }

  private centerPopover(): void {
    // No target: center the popover (welcome-style step).
    this.spotlight = null;
    this.popover = {
      top: Math.max(window.innerHeight / 2 - 90, 16),
      left: Math.max(window.innerWidth / 2 - GuidedTourComponent.POPOVER_WIDTH / 2, 16),
      arrow: 'none'
    };
  }

  /**
   * Returns the first element matching the selector that is actually rendered and laid out
   * (non-zero size). This avoids anchoring to hidden duplicates (e.g. a tab chevron inside a tab
   * hidden by the sidebar view filter), which would otherwise report a 0,0 rect.
   */
  private findVisibleTarget(selector: string): HTMLElement {
    const candidates = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
    return candidates.find(el => {
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }) || null;
  }

  private positionPopoverNextTo(box: Box): void {
    const gap = GuidedTourComponent.POPOVER_GAP;
    const width = GuidedTourComponent.POPOVER_WIDTH;
    const spaceOnRight = window.innerWidth - (box.left + box.width);

    if (spaceOnRight >= width + gap) {
      // Preferred: to the right of the target (sidebars sit on the left edge).
      this.popover = {
        top: this.clampPopoverTop(box.top),
        left: box.left + box.width + gap,
        arrow: 'left'
      };
    } else {
      // Fallback: below the target.
      this.popover = {
        top: this.clampPopoverTop(box.top + box.height + gap),
        left: Math.min(box.left, window.innerWidth - width - 16),
        arrow: 'top'
      };
    }
  }

  /**
   * Keeps the popover fully within the viewport so its content is never pushed below (or above)
   * the screen — e.g. when anchoring to an icon near the bottom of a tall sidebar.
   */
  private clampPopoverTop(top: number): number {
    const margin = GuidedTourComponent.VIEWPORT_MARGIN;
    const height = this.measurePopoverHeight();
    const maxTop = Math.max(margin, window.innerHeight - height - margin);
    return Math.min(Math.max(top, margin), maxTop);
  }

  /** Current popover height, falling back to an estimate before it is first rendered. */
  private measurePopoverHeight(): number {
    const el = document.querySelector('.guided-tour__popover') as HTMLElement;
    return el?.offsetHeight || GuidedTourComponent.POPOVER_ESTIMATED_HEIGHT;
  }
}




